import React, { useState, useRef } from "react";
import {
  PresentationChartLineIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ChartPieIcon,
  HashtagIcon,
} from "@heroicons/react/24/outline";
import RefreshButton from "../RefreshButton";

const StoriesAnalyticsTab = ({
  activeHoursData,
  popularSearchesData,
  categoryInterestData,
  analyticsDays,
  analyticsLoading,
  onDaysChange,
  onRefresh,
}) => {
const hoursData = activeHoursData?.data || [];
  const maxViews = hoursData.length > 0 ? Math.max(...hoursData.map((d) => d.view_count)) : 0;

  const [tooltip, setTooltip] = useState(null);
  const blockRef = useRef(null);

  const showTooltip = (item, e) => {
    const blockRect = blockRef.current.getBoundingClientRect();
    const colRect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      item,
      x: colRect.left + colRect.width / 2 - blockRect.left,
      y: colRect.top - blockRect.top,
    });
  };
  const hideTooltip = () => setTooltip(null);

  return (
  <>
  <style>{`
    .ah-scroll::-webkit-scrollbar { height: 8px; }
    .ah-scroll::-webkit-scrollbar-track { background: var(--bg-secondary); border-radius: 4px; }
    .ah-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 4px; }
    .ah-wrap:hover .ah-scroll::-webkit-scrollbar-thumb { background: var(--border-accent); border-radius: 4px; }
    .ah-wrap:hover .ah-scroll::-webkit-scrollbar-thumb:hover { background: var(--text-muted); }
    .ah-scroll { scrollbar-color: transparent transparent; }
    .ah-wrap:hover .ah-scroll { scrollbar-color: var(--border-accent) var(--bg-secondary); }
    .light-theme .ah-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
    .light-theme .ah-wrap:hover .ah-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; }
    .light-theme .ah-wrap:hover .ah-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
    .light-theme .ah-wrap:hover .ah-scroll { scrollbar-color: #c1c1c1 #f1f1f1; }
    /* Tooltip */
    .ah-tooltip { background: #ffffff; border: 1px solid #E5E7EB; box-shadow: 0 8px 24px rgba(0,0,0,0.12); }
    .ah-tooltip-title { color: #111827; }
    .ah-tooltip-label { color: #6B7280; }
    .ah-tooltip-value { color: #111827; }
    .ah-tooltip-arrow-border { border-top-color: #E5E7EB !important; }
    .ah-tooltip-arrow-fill { border-top-color: #ffffff !important; }
    .dark-theme .ah-tooltip { background: #222630; border: 1px solid rgba(255,255,255,0.25); box-shadow: 0 8px 24px rgba(0,0,0,0.5); }
    .dark-theme .ah-tooltip-title { color: #ffffff; }
    .dark-theme .ah-tooltip-label { color: #9CA3AF; }
    .dark-theme .ah-tooltip-value { color: #ffffff; }
    .dark-theme .ah-tooltip-arrow-border { border-top-color: rgba(255,255,255,0.25) !important; }
    .dark-theme .ah-tooltip-arrow-fill { border-top-color: #222630 !important; }
  `}</style>
  <div className="space-y-6">
    {/* Combined block: Header + Stat cards + Active Hours */}
    <div className="block-bg rounded-block p-6">
      {/* Header with period selector */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center space-x-2">
            <PresentationChartLineIcon className="w-7 h-7" />
            <span>Сториз аналитикасы</span>
          </h2>
          <p className="text-gray-400">
            Пайдаланушылардың белсенділігі мен іздеулер аналитикасы
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Кезең (күн):</label>
            <div className="relative flex items-center">
              <svg width="16" height="18" viewBox="0 0 21 23" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute left-3 pointer-events-none text-gray-400 flex-shrink-0">
                <path d="M4.94845 0.0662975C4.76446 0.153952 4.67041 0.245985 4.57637 0.443203C4.50686 0.58783 4.49869 0.710545 4.49869 1.50818V2.40662H3.24343C2.26621 2.40662 1.92684 2.42415 1.7224 2.47236C0.896466 2.68272 0.197284 3.46721 0.0459988 4.3525C-0.0153329 4.71626 -0.0153329 20.1212 0.0459988 20.4893C0.197284 21.3921 0.974153 22.2248 1.81644 22.387C2.1599 22.4527 18.7767 22.4527 19.1202 22.387C19.9625 22.2248 20.7393 21.3921 20.8906 20.4893C20.952 20.1212 20.952 4.71626 20.8906 4.3525C20.7393 3.46721 20.0402 2.68272 19.2142 2.47236C19.0098 2.42415 18.6704 2.40662 17.6973 2.40662H16.4379V1.49503C16.4379 0.63604 16.4298 0.579065 16.3439 0.412525C16.2948 0.316109 16.1926 0.193394 16.1149 0.140802C15.751 -0.122154 15.2767 0.000558853 15.06 0.412525C14.9742 0.579065 14.966 0.63604 14.966 1.49503V2.40662H10.4683H5.97065V1.50818C5.97065 0.710545 5.96247 0.58783 5.89296 0.443203C5.79892 0.241604 5.70488 0.149569 5.50862 0.0619164C5.32462 -0.0213547 5.13245 -0.0213547 4.94845 0.0662975ZM4.49869 4.94854C4.51095 5.54896 4.53548 5.92148 4.56819 5.97845C4.64179 6.11432 4.83397 6.29839 4.96072 6.3466C5.2224 6.45616 5.53724 6.38604 5.73759 6.18444C5.94203 5.97845 5.9502 5.93901 5.97065 4.94854L5.99109 4.00628H10.4683H14.9455L14.966 4.94854C14.9782 5.54896 15.0028 5.92148 15.0355 5.97845C15.1091 6.11432 15.3013 6.29839 15.428 6.3466C15.6897 6.45616 16.0045 6.38604 16.2049 6.18444C16.4093 5.97845 16.4175 5.93901 16.4379 4.94854L16.4584 4.00628H17.6646C18.8381 4.00628 18.8749 4.01066 19.0425 4.10269C19.1488 4.16843 19.2551 4.27361 19.3205 4.39195L19.4228 4.58478V6.29839V8.01637H10.4683H1.51387V6.29839V4.58478L1.61609 4.39195C1.7224 4.20349 1.87777 4.08516 2.10675 4.02381C2.17626 4.00628 2.73642 3.99751 3.35383 3.99751L4.47824 4.00628L4.49869 4.94854ZM19.4228 14.9278V20.257L19.3246 20.4367C19.2715 20.5375 19.1529 20.6602 19.0629 20.7172L18.8994 20.8136H10.4683H2.03724L1.87369 20.7172C1.78373 20.6602 1.66516 20.5375 1.612 20.4367L1.51387 20.257V14.9278V9.59412H10.4683H19.4228V14.9278Z" fill="currentColor"/>
                <path d="M14.6595 11.2989C14.5491 11.3559 13.6169 12.3245 11.7565 14.3229C10.2518 15.9401 9.00061 17.2637 8.97608 17.2637C8.95155 17.2637 8.37503 16.6589 7.68811 15.9226C7.0012 15.1863 6.38788 14.5552 6.31837 14.5158C6.163 14.4281 5.81954 14.4281 5.65599 14.5158C5.38613 14.6692 5.19804 15.0548 5.2512 15.3572C5.26755 15.4361 5.33297 15.5851 5.39839 15.6903C5.46381 15.7955 6.18344 16.5975 7.0012 17.4697C8.51405 19.0868 8.6776 19.2315 8.9679 19.2359C9.02923 19.2359 9.14781 19.2052 9.23367 19.1701C9.4422 19.0737 15.514 12.583 15.6244 12.3332C15.7103 12.1316 15.7185 12.044 15.6612 11.8205C15.604 11.6013 15.4364 11.3953 15.2319 11.2989C15.0234 11.1937 14.8639 11.1981 14.6595 11.2989Z" fill="currentColor"/>
              </svg>
              <select
                value={analyticsDays}
                onChange={(e) => onDaysChange(Number(e.target.value))}
                className="pl-9 pr-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value={1}>1 күн</option>
                <option value={7}>7 күн</option>
                <option value={14}>14 күн</option>
                <option value={30}>30 күн</option>
              </select>
            </div>
          </div>
          <RefreshButton onClick={onRefresh} label="Жаңарту" />
        </div>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="relative overflow-hidden rounded-card border border-border-light dark:border-border-light/10 bg-white dark:bg-bg-dark-card p-6">
          <div className="absolute inset-0 bg-accent-grad opacity-[0.01] pointer-events-none" />
          <h3 className="text-sm font-medium text-[#6C757D] dark:text-[#9CA3AF]">Жалпы көріністер саны</h3>
          <p className="text-3xl font-bold bg-blue-grad bg-clip-text text-transparent mt-2">
            {(activeHoursData?.total_views || 0).toLocaleString()}
          </p>
        </div>
        <div className="relative overflow-hidden rounded-card border border-border-light dark:border-border-light/10 bg-white dark:bg-bg-dark-card p-6">
          <div className="absolute inset-0 bg-accent-grad opacity-[0.01] pointer-events-none" />
          <h3 className="text-sm font-medium text-[#6C757D] dark:text-[#9CA3AF]">Жалпы бірегей пайдаланушылар</h3>
          <p className="text-3xl font-bold bg-green-grad bg-clip-text text-transparent mt-2">
            {(activeHoursData?.total_unique_users || 0).toLocaleString()}
          </p>
        </div>
        <div className="relative overflow-hidden rounded-card border border-border-light dark:border-border-light/10 bg-white dark:bg-bg-dark-card p-6">
          <div className="absolute inset-0 bg-accent-grad opacity-[0.01] pointer-events-none" />
          <h3 className="text-sm font-medium text-[#6C757D] dark:text-[#9CA3AF]">Жалпы іздеулер саны</h3>
          <p className="text-3xl font-bold bg-purple-grad bg-clip-text text-transparent mt-2">
            {(popularSearchesData?.total_searches || 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Active Hours */}
      <div ref={blockRef} className="ah-wrap relative rounded-card bg-white dark:bg-bg-dark-card p-6 border border-border-light dark:border-border-light/10">
        <div className="absolute inset-0 bg-accent-grad opacity-[0.01] pointer-events-none rounded-card" />

        {/* Header */}
        <div className="flex items-center mb-6">
          <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
            <ClockIcon className="w-6 h-6" />
            <span>Белсенділік сағаттары</span>
          </h3>
        </div>

        {/* Chart */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {/* Y-axis */}
          <div style={{ display: 'flex', flexDirection: 'column', height: '325px', paddingTop: '14px', minWidth: '40px', textAlign: 'right' }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: 1 }}>
              {[maxViews, Math.round(maxViews * 0.75), Math.round(maxViews * 0.5), Math.round(maxViews * 0.25), 0].map((v, i) => (
                <span key={i} style={{ fontSize: '10px', color: '#9CA3AF', lineHeight: 1 }}>{v}</span>
              ))}
            </div>
          </div>

          {/* Bars + x-axis + legend */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="ah-scroll" style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '33px', height: '325px', paddingTop: '14px', minWidth: 'max-content' }}>
                {hoursData.map(item => {
                  const CHART_H = 281;
                  return (
                    <div
                      key={item.hour}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0, cursor: 'pointer' }}
                      onMouseEnter={(e) => showTooltip(item, e)}
                      onMouseLeave={hideTooltip}
                    >
                      {/* Колба — всегда полная высота, заливка снизу */}
                      <div
                        className="border border-black/25 dark:border-white/25"
                        style={{
                          position: 'relative', width: '24px', height: `${CHART_H}px`,
                          borderRadius: '7px 7px 0 0',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                        }}
                      >
                        {/* Көріністер — blue */}
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          height: `${maxViews > 0 ? (item.view_count / maxViews) * 100 : 0}%`,
                          background: 'linear-gradient(to top, #2563EB 0%, #5C17E7 100%)',
                          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
                        }} />
                        {/* Бірегей пайдаланушылар — green, поверх */}
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          height: `${maxViews > 0 ? (item.unique_users / maxViews) * 100 : 0}%`,
                          background: 'linear-gradient(to top, #60C85B 0%, #44C673 100%)',
                          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
                        }} />
                      </div>
                      <span style={{ fontSize: '16px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>{item.hour}:00</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Legend — выровнена по левому краю баров */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '30px', height: '12px', borderRadius: '100px', background: 'linear-gradient(to top, #2563EB 0%, #5C17E7 100%)', flexShrink: 0 }} />
                <span className="text-[#4A4A4A] dark:text-[#B3B3B3]" style={{ fontSize: '16px', fontWeight: 500, whiteSpace: 'nowrap' }}>Көріністер</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '30px', height: '12px', borderRadius: '100px', background: 'linear-gradient(to top, #60C85B 0%, #44C673 100%)', flexShrink: 0 }} />
                <span className="text-[#4A4A4A] dark:text-[#B3B3B3]" style={{ fontSize: '16px', fontWeight: 500, whiteSpace: 'nowrap' }}>Бірегей пайдаланушылар</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tooltip */}
        {tooltip && (
          <div className="ah-tooltip" style={{
            position: 'absolute',
            top: tooltip.y - 120,
            left: tooltip.x,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 50,
            borderRadius: '10px',
            padding: '12px 16px',
            minWidth: '230px',
            whiteSpace: 'nowrap',
          }}>
            <div className="ah-tooltip-title" style={{ fontWeight: 700, fontSize: '16px', marginBottom: '10px' }}>
              {String(tooltip.item.hour).padStart(2, '0')}:00
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '7px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'linear-gradient(135deg, #2563EB, #5C17E7)', flexShrink: 0 }} />
                <span className="ah-tooltip-label" style={{ fontSize: '14px' }}>Барлық көріністер:</span>
              </div>
              <span className="ah-tooltip-value" style={{ fontSize: '14px', fontWeight: 700 }}>{tooltip.item.view_count.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'linear-gradient(135deg, #60C85B, #44C673)', flexShrink: 0 }} />
                <span className="ah-tooltip-label" style={{ fontSize: '14px' }}>Бірегей пайдаланушылар:</span>
              </div>
              <span className="ah-tooltip-value" style={{ fontSize: '14px', fontWeight: 700 }}>{tooltip.item.unique_users.toLocaleString()}</span>
            </div>
            {/* Arrow border */}
            <div className="ah-tooltip-arrow-border" style={{
              position: 'absolute', bottom: '-9px', left: '50%', transform: 'translateX(-50%)',
              width: 0, height: 0,
              borderLeft: '9px solid transparent', borderRight: '9px solid transparent',
              borderTop: '9px solid',
            }} />
            {/* Arrow fill */}
            <div className="ah-tooltip-arrow-fill" style={{
              position: 'absolute', bottom: '-8px', left: '50%', transform: 'translateX(-50%)',
              width: 0, height: 0,
              borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
              borderTop: '8px solid',
            }} />
          </div>
        )}
      </div>
    </div>

    {/* Popular Searches */}
    <div className="block-bg rounded-block p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
          <MagnifyingGlassIcon className="w-6 h-6" />
          <span>Танымал іздеулер</span>
        </h3>
      </div>
      {analyticsLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 animate-pulse">
              <div className="h-6 bg-gray-700 rounded w-1/3 mb-4" />
              <div className="h-4 bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      ) : popularSearchesData?.queries ? (
        <div className="space-y-3">
          {popularSearchesData.queries.length > 0 ? (
            popularSearchesData.queries.map((query, index) => (
              <div
                key={index}
                className="p-4 bg-gray-900/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-12 h-12 bg-accent-grad rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-base">
                          #{index + 1}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-lg">
                          {query.query}
                        </h4>
                        {query.category_name && (
                          <div className="flex items-center space-x-2 mt-1">
                            <HashtagIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-400">
                              {query.category_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0 px-4 py-2 rounded-lg" style={{ background: 'rgba(123,37,235,0.1)', border: '1px solid rgba(123,37,235,0.2)' }}>
                    <div className="text-2xl font-bold bg-purple-grad bg-clip-text text-transparent">{query.count}</div>
                    <div className="text-xs bg-purple-grad bg-clip-text text-transparent">рет</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Іздеулер табылмады</p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Деректер жоқ</p>
        </div>
      )}
    </div>

    {/* Category Interest */}
    <div className="block-bg rounded-block p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
          <ChartPieIcon className="w-6 h-6" />
          <span>Категорияларға деген қызығушылық</span>
        </h3>
      </div>
      {analyticsLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 animate-pulse">
              <div className="h-6 bg-gray-700 rounded w-1/3 mb-4" />
              <div className="h-4 bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      ) : categoryInterestData?.categories ? (
        <div className="space-y-3">
          {categoryInterestData.categories.length > 0 ? (
            categoryInterestData.categories.map((category) => {
              const maxViews = Math.max(
                ...categoryInterestData.categories.map((c) => c.view_count),
              );
              return (
                <div
                  key={category.category_id}
                  className="p-5 bg-gray-900/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-accent-grad rounded-lg flex items-center justify-center flex-shrink-0">
                        <HashtagIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-lg">
                          {category.category_name}
                        </h4>
                        <p className="text-sm text-gray-400">
                          ID: {category.category_id}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-bg-dark-card p-4 border border-border-light dark:border-border-dark-inner rounded-card">
                      <div className="text-xs text-gray-400 mb-1">Көріністер</div>
                      <div className="text-2xl font-bold bg-blue-grad bg-clip-text text-transparent">
                        {category.view_count}
                      </div>
                      <div className="mt-2 bg-[#F1F3F5] dark:bg-[#374151] rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-blue-grad h-full transition-all duration-300"
                          style={{
                            width: `${maxViews > 0 ? (category.view_count / maxViews) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="bg-white dark:bg-bg-dark-card p-4 border border-border-light dark:border-border-dark-inner rounded-card">
                      <div className="text-xs text-gray-400 mb-1">Бірегей пайдаланушылар</div>
                      <div className="text-2xl font-bold bg-green-grad bg-clip-text text-transparent">
                        {category.unique_users}
                      </div>
                      <div className="mt-2 bg-[#F1F3F5] dark:bg-[#374151] rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-green-grad h-full transition-all duration-300"
                          style={{
                            width: `${maxViews > 0 ? (category.unique_users / maxViews) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="bg-white dark:bg-bg-dark-card p-4 border border-border-light dark:border-border-dark-inner rounded-card">
                      <div className="text-xs text-gray-400 mb-1">Іздеулер</div>
                      <div className="text-2xl font-bold bg-purple-grad bg-clip-text text-transparent">
                        {category.search_count}
                      </div>
                      <div className="mt-2 bg-[#F1F3F5] dark:bg-[#374151] rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-purple-grad h-full transition-all duration-300"
                          style={{
                            width: `${maxViews > 0 ? (category.search_count / maxViews) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-gray-500">
              <ChartPieIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Категориялар табылмады</p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <ChartPieIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Деректер жоқ</p>
        </div>
      )}
    </div>
  </div>
  </>
  );
};

export default StoriesAnalyticsTab;
